import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  CButton,
  CDropdown,
  CDropdownToggle,
  CDropdownMenu,
  CDropdownItem,
} from '@coreui/react-pro'
import { format } from 'date-fns'
import FEATURES from '../../../../../utils/featuresConstants'
import { getCptEncTypeMismatch, VISIT_STATUSES } from './panelSummaryHelpers'
import { COLLECTOR_URL } from '../../../../../apiHelper/apiRoute/collector'
import { postCollector, putCollector } from '../../../../../apiHelper/collectorApi'
import { resetProviderHold } from '../../../../../redux/providerHold/providerHoldSlice'
import { toast } from 'react-toastify'
import { refreshEncounter, saveEncounterField } from '../../../../../redux/sidePanel/sidePanelThunk'
import {
  canProviderAccessNote,
  downloadChartPDF,
  getIsNoteRequired,
  getNoteStatus,
  shouldHidePrivateNote,
} from '../../../../../utils/sharedChartFunctions'
import { VISIT_STATUS } from '../../../../../utils/statusConstants'
import { billVisitSubmit, setVisitVoid } from '../../../../../helper/backendHelper'
import {
  addlVisit,
  recallBill,
  skipVisit,
  stopVisitTracking,
  undoLastVisit,
  undoSkipVisit,
} from '../../../../../components/common/helper/statusUtil'
import { clearSidePanel, setAutoSaveDraft } from '../../../../../redux/sidePanel/sidePanelSlice'
import Void from '../../../census/components/ConfirmationModals/Void'
import RecallSubmissionBulk from '../../../census/components/ConfirmationModals/RecallSubmissionBulk'
import ClaimocitySupportPopup from '../../../../../components/common/ClaimocitySupportPopUp/ClaimocitySupportPopUp'
import DeleteNote from '../../../census/components/ConfirmationModals/DeleteNote'
import {
  billVisitErrorCheck,
  canShowDischarge,
  canShowUndoDischarge,
  isVisitDischarged,
} from '../../../../../components/common/shared/sharedEncounterLogic'
import { getChartValidations } from '../../../../../helper/chartValidationHelper'
import { hasMedicalCoding } from '../../../../../components/common/shared/sharedProviderDisplayLogic'
import { ENCOUNTER_TYPES, SYSTEM_ID } from '../../../sharedConstants'
import CloneEncounterDateModal from './CloneEncounterDateModal'

export default function PanelSummarySignButtons({ visitStatus }) {
  const dispatch = useDispatch()
  const features = useSelector((state) => state.user?.features) || []
  const roles = useSelector((state) => state.user?.roles) || []
  const user = useSelector((state) => state.user?.user)
  const provider = useSelector((state) => state.user?.provider)
  const practice = useSelector((state) => state.user?.practice)

  const visitStatuses = useSelector((state) => state.census?.visitStatuses) || {}
  const thisDate = useSelector((state) => state.census.activeDate) || new Date()

  const panelChart = useSelector((state) => state.sidePanel?.panelChart)
  const panelEncounter = useSelector((state) => state.sidePanel?.panelEncounter)
  const encounterId = useSelector((state) => state.sidePanel.panelEncounter?._id)
  const encounterType = useSelector((state) => state.sidePanel.panelEncounter?.encounterType)
  const dxCodes = useSelector((state) => state.sidePanel?.panelEncounter?.diagnosisCodes)
  const cptCodes = useSelector((state) => state.sidePanel?.panelEncounter?.procedureCodes)
  const isLastEncounter = useSelector((state) => state.sidePanel.isLastEncounter)
  const panelMipsError = useSelector((state) => state.sidePanel.panelMipsError)

  const [validCPTSection, setValidCPTSection] = useState(false)
  const [validDxSection, setValidDxSection] = useState(false)

  const [cptValidForEncType, setCptValidForEncType] = useState(false)

  const [disableButtons, setDisableButtons] = useState(false)
  const [showVoid, setShowVoid] = useState(false)
  const [showRecall, setShowRecall] = useState(false)
  const [showMessaging, setShowMessaging] = useState(false)
  const [showDelete, setShowDelete] = useState(false)
  const [showClone, setShowClone] = useState(false)

  const noteRequired = getIsNoteRequired(practice, provider, roles)

  const isBillerExempt = hasMedicalCoding({ practice, provider, roles, billerCheck: true })

  useEffect(() => {
    if (isBillerExempt) {
      setValidCPTSection(true)
      setCptValidForEncType(true)
    } else {
      setValidCPTSection(cptCodes?.length > 0)
      setCptValidForEncType(
        !Boolean(
          getCptEncTypeMismatch({
            encounterType,
            emCode: cptCodes?.[0],
          }),
        ),
      )
    }

    setValidDxSection(dxCodes?.length > 0)
  }, [isBillerExempt, cptCodes, dxCodes, encounterType])

  const limitCopying = practice?.restrictVisitCloningToProvider
  const encounterMatchesCurrentProvider = provider?._id === panelEncounter?.rendering_provider_id
  const showCopyButton =
    (!limitCopying || encounterMatchesCurrentProvider) && panelChart?.signed_user_id !== SYSTEM_ID

  const onSignAndBill = async (encId) => {
    const billHasErrors = billVisitErrorCheck({
      chart: panelChart,
      chartValidations: await getChartValidations(),
      diagnosisCodes: panelEncounter?.diagnosisCodes,
      encounterType: panelEncounter?.encounterType,
      procedureCodes: panelEncounter?.procedureCodes,
      practice,
      provider,
      roles,
      service_date_from: panelEncounter?.service_date_from,
      userId: user?._id,
    })

    if (billHasErrors) {
      // toasts go off in error check
      return
    }

    if (panelMipsError && !isBillerExempt) {
      toast.error(`MIPS must be completed.`)
      return
    }

    if (!encId) {
      toast.error('Unable to sign and bill visit, missing info')
      return
    }

    // submit bill separately to avoid rendering provider mistakes
    const billVisitRes = await billVisitSubmit(encId)
    if (billVisitRes?.status === 'success') {
      dispatch(clearSidePanel())
      toast.success('Encounter Signed')
    } else if (billVisitRes.response?.data?.message || billVisitRes.response?.data?.result) {
      const { message, result } = billVisitRes.response.data
      toast.error(message || result)
    }
  }

  const onAddToCensus = async () => {
    if (!panelEncounter.facility_id) {
      return toast.error('No facility attached to this encounter')
    }
    setDisableButtons(true)

    let res = {}
    if (panelEncounter?._id) {
      if (isLastEncounter) {
        const url = COLLECTOR_URL.CLONE.replace('{visitId}', panelEncounter._id)
        const req = {
          service_date: thisDate,
          status: VISIT_STATUS.checked_in,
          last_visit: null,
          encounterType: ENCOUNTER_TYPES.SUBSEQUENT,
          facility_id: panelEncounter.facility_id,
        }
        res = await postCollector(url, req)
      } else {
        const url = COLLECTOR_URL.VISIT_STATUS.replace('{visitId}', panelEncounter._id)
        res = await putCollector(
          url,
          // the status we would prefer the visit to update to
          // if its in an 'unseen' state
          { status: VISIT_STATUS.checked_in },
        )
      }
    } else {
      // is new visit
      const req = {
        service_date: thisDate,
        status: VISIT_STATUS.checked_in,
        last_visit: null,
        room: '',
        facility_id: panelEncounter.facility_id,
        facility_mrn: panelEncounter?.facility_mrn,
        patient_id: panelEncounter?.patient_id,
        patient: {
          patient_id: panelEncounter?.patient_id,
          patient_name: panelEncounter?.patient_name,
          patient_type: panelEncounter?.patient_type,
        },
        procedure_codes: [],
        diagnosis_codes: [],
        files: [],
        code_assist: {
          diagnosis_notes: '',
          billing_notes: '',
          audio_notes: [],
          templates: [],
        },
        encounterType: ENCOUNTER_TYPES.INITIAL,
        care_coordination: false,
        long_term_care: false,
        auth_number: '',
        careteam: '',
        hospice_related: false,
        telehealth: false,
        isSaveOnly: true,
        admission_reason: '',
      }
      res = await postCollector(COLLECTOR_URL.ADD_ENCOUNTER, req)
    }

    if (res?.status === 'success') {
      dispatch(refreshEncounter({ encounterId: res?.result?._id || panelEncounter?._id }))
      toast.success('Successfully added encounter to Census')
    } else {
      toast.error(
        res?.result || res?.response?.data?.message || res?.message || 'Unable to Add to Census',
      )
    }

    setDisableButtons(false)
  }

  const onUndoDischarge = async () => {
    setDisableButtons(true)

    const resp = await undoLastVisit([panelEncounter])
    if (resp.sucessVisitsCount > 0) {
      dispatch(refreshEncounter({ encounterId: encounterId }))
      toast.success('Discharge undone')
    } else {
      toast.error('Unable to undo discharge')
    }
    setDisableButtons(false)
  }

  const onDischarge = async () => {
    setDisableButtons(true)
    const visitToDischarge = {
      ...panelEncounter,
      status: isLastEncounter ? VISIT_STATUS.last : panelEncounter.status,
    }

    const resp = await stopVisitTracking([visitToDischarge], thisDate)
    if (resp.sucessVisitsCount > 0) {
      dispatch(
        refreshEncounter({
          encounterId: resp.allSuccessVisits?.[0]?.charge_id || encounterId,
        }),
      )
      toast.success('Patient visit discharged')
    } else {
      toast.error('Unable to discharge')
    }
    setDisableButtons(false)
  }

  const onAdditionalVisit = async () => {
    setDisableButtons(true)
    const updateStatus = await addlVisit(panelEncounter, thisDate)
    if (updateStatus.sucessVisitsCount > 0) {
      dispatch(
        refreshEncounter({
          encounterId: updateStatus?.newVisit?._id,
          resetClonedFields: true,
        }),
      )
      toast.success('Additional Visit created')
    } else {
      toast.error('Additional Visit already exists for this patient on this date of service')
    }
    setDisableButtons(false)
  }

  const onRecall = async () => {
    setDisableButtons(true)
    const resp = await recallBill([panelEncounter])
    if (resp.sucessVisitsCount > 0) {
      dispatch(refreshEncounter({ encounterId }))
      toast.success('Visit recalled')
    } else {
      toast.error('Unable to recall visit')
    }
    setDisableButtons(false)
  }

  const onVoid = async () => {
    setDisableButtons(true)
    let res = await setVisitVoid(panelEncounter?._id)
    if (res?.status === 'success') {
      dispatch(clearSidePanel())
      toast.success('Visit voided')
    } else {
      toast.error('Unable to void visit')
    }

    setDisableButtons(false)
  }

  const onSkip = async () => {
    setDisableButtons(true)
    const visitToSkip = {
      ...panelEncounter,
      status: isLastEncounter ? VISIT_STATUS.last : panelEncounter.status,
    }

    const res = await skipVisit([visitToSkip], thisDate)
    if (res.sucessVisitsCount > 0) {
      const newEncId = res?.successfulVisits?.[0]?.result?._id || res?.successfulVisits?.[0]?._id
      dispatch(refreshEncounter({ encounterId: newEncId }))
      toast.success('Visit skipped')
    } else {
      toast.error('Unable to skip visit')
    }
    setDisableButtons(false)
  }

  const onUndoSkip = async () => {
    setDisableButtons(true)

    const res = await undoSkipVisit([panelEncounter])
    if (res.sucessVisitsCount > 0) {
      dispatch(refreshEncounter({ encounterId }))
      toast.success('Visit skip undone')
    } else {
      toast.error('Unable to skip visit')
    }
    setDisableButtons(false)
  }

  const onClone = async (cloneDate) => {
    if (!panelEncounter.facility_id) {
      return toast.error('No facility attached to this encounter')
    }
    if (!panelEncounter._id) {
      return toast.error('Unable to clone encounter')
    }
    setDisableButtons(true)

    const data = {
      service_date: cloneDate,
      status: VISIT_STATUS.checked_in,
      last_visit: null,
      encounterType: ENCOUNTER_TYPES.SUBSEQUENT,
      facility_id: panelEncounter.facility_id,
    }
    const res = await postCollector(
      COLLECTOR_URL.CLONE.replace('{visitId}', panelEncounter._id),
      data,
    )

    if (res?.status === 'success') {
      dispatch(refreshEncounter({ encounterId: res?.result?._id || panelEncounter?._id }))
      toast.success('Successfully cloned encounter')
    } else {
      toast.error(
        res?.result ||
          res?.response?.data?.message ||
          res?.message ||
          'Unable to clone the encounter',
      )
    }

    setDisableButtons(false)
  }

  const onSignBill = async () => {
    setDisableButtons(true)
    // save before signing, just in case
    let save = await dispatch(saveEncounterField({}))
    if (panelChart?._id) {
      dispatch(setAutoSaveDraft(true))
    }

    // if a salt bill, make sure we use the new _id
    let saltIdUpdate = save?.payload?.cloneUpdates?.find(
      (update) => update?.payload?.field === '_id',
    )

    // avoid race condition where saving the encounter can overwrite the bill
    setTimeout(() => {
      onSignAndBill(saltIdUpdate?.payload?.value || panelEncounter._id).then(() => {
        setDisableButtons(false)
      })
    }, 1500)
  }

  const doNotShowSignButton =
    !validCPTSection ||
    !validDxSection ||
    !cptValidForEncType ||
    disableButtons ||
    (panelMipsError && !isBillerExempt) ||
    (noteRequired && !panelChart?._id) ||
    visitStatus === VISIT_STATUSES.NotSeen ||
    isVisitDischarged(panelEncounter)

  return (
    <div id={'PanelSummarySignButtons'} className={'d-flex gap-2 align-items-center'}>
      {visitStatus === VISIT_STATUSES.Hold ? (
        <CButton
          className={'signButton'}
          onClick={async () => {
            setDisableButtons(true)
            // save encounter
            await dispatch(saveEncounterField({}))
            // TODO ensure imgs added through here work
            const url = COLLECTOR_URL.BILL_STATUS_CHANGE.replace('{visit_id}', panelEncounter._id)
            const resp = await postCollector(url, {})
            if (resp.status === 'success') {
              dispatch(clearSidePanel())
              toast.success('Visit Resolved')
            } else {
              toast.error(
                resp?.response?.data?.message?.result ||
                  resp?.response?.data?.result ||
                  'Visit Failed to Resolve',
              )
            }
            setDisableButtons(false)
          }}
          disabled={!validCPTSection || !validDxSection || !cptValidForEncType || disableButtons}
        >
          Resolve Hold
        </CButton>
      ) : (
        visitStatus !== VISIT_STATUSES.Completed && (
          <CButton
            className={'signButton'}
            onClick={onSignBill}
            disabled={isLastEncounter || doNotShowSignButton || disableButtons}
          >
            {console.log({
              isLastEncounter,
              validCPTSection,
              validDxSection,
              cptValidForEncType,
              disableButtons,
              panelMipsError,
              noteRequired,
              skipDisch:
                visitStatus === VISIT_STATUSES.NotSeen || isVisitDischarged(panelEncounter),
            })}
            Sign Bill
          </CButton>
        )
      )}
      <CDropdown className={'summary-dropdown'}>
        <CDropdownToggle color="secondary">
          <i className="fa-solid fa-circle-ellipsis"></i>
        </CDropdownToggle>
        <CDropdownMenu className={`summary-dropdown-menu`}>
          {showCopyButton && (
            <CDropdownItem disabled={disableButtons} onClick={() => setShowClone(true)}>
              Clone Encounter
            </CDropdownItem>
          )}
          {visitStatus === VISIT_STATUSES.Available &&
            !doNotShowSignButton &&
            !panelEncounter.incident_to_provider_id && (
              <CDropdownItem disabled={disableButtons} onClick={onSignBill}>
                Bill SALT
              </CDropdownItem>
            )}

          {(visitStatus === VISIT_STATUSES.Available || visitStatus === VISIT_STATUSES.NotSeen) && (
            <CDropdownItem disabled={disableButtons} onClick={onAddToCensus}>
              Add to Census
            </CDropdownItem>
          )}

          {(visitStatus === VISIT_STATUSES.Draft || visitStatus === VISIT_STATUSES.Available) && (
            <CDropdownItem disabled={disableButtons} onClick={onSkip}>
              Skip
            </CDropdownItem>
          )}

          {/*{visitStatus === VISIT_STATUSES.NotSeen && (*/}
          {/*  <CDropdownItem disabled={disableButtons} onClick={onUndoSkip}>*/}
          {/*    Undo Skip*/}
          {/*  </CDropdownItem>*/}
          {/*)}*/}

          {(visitStatus === VISIT_STATUSES.Draft ||
            visitStatus === VISIT_STATUSES.Available ||
            visitStatus === VISIT_STATUSES.Completed) &&
            canShowDischarge(panelEncounter) && (
              <CDropdownItem disabled={disableButtons} onClick={onDischarge}>
                Mark as Last
              </CDropdownItem>
            )}
          {(visitStatus === VISIT_STATUSES.NotSeen || isVisitDischarged(panelEncounter)) &&
            canShowUndoDischarge(panelEncounter) && (
              <CDropdownItem disabled={disableButtons} onClick={onUndoDischarge}>
                Undo Mark as Last
              </CDropdownItem>
            )}

          {(visitStatus === VISIT_STATUSES.Draft ||
            visitStatus === VISIT_STATUSES.Hold ||
            visitStatus === VISIT_STATUSES.NotSeen) &&
            visitStatuses[panelEncounter?.status]?.actions?.includes('Void') &&
            !shouldHidePrivateNote(panelChart, user?._id) &&
            canProviderAccessNote(panelEncounter, provider) && (
              <CDropdownItem disabled={disableButtons} onClick={() => setShowVoid(true)}>
                Void
              </CDropdownItem>
            )}
          {features?.includes(FEATURES.VisitNotesPOC) &&
            panelChart?._id &&
            panelEncounter?.patient_id &&
            (visitStatus === VISIT_STATUSES.Draft ||
              visitStatus === VISIT_STATUSES.Hold ||
              visitStatus === VISIT_STATUSES.Completed) &&
            (getNoteStatus({ chart: panelChart }, true) === 'draft' ||
              getNoteStatus({ chart: panelChart }, true) === 'pending_review') &&
            // if private, no one else should see the button
            !shouldHidePrivateNote(panelChart, user?._id) &&
            // limit provider scope on who can delete notes
            canProviderAccessNote(panelEncounter, provider) && (
              <CDropdownItem disabled={disableButtons} onClick={() => setShowDelete(true)}>
                Delete Note
              </CDropdownItem>
            )}

          {visitStatus === VISIT_STATUSES.Completed && (
            <>
              <CDropdownItem disabled={disableButtons} onClick={() => setShowRecall(true)}>
                Recall Submission
              </CDropdownItem>
              {features?.includes(FEATURES.AdditionalVisit) &&
                visitStatuses[panelEncounter?.status]?.actions?.includes('Additional encounter') &&
                panelEncounter.rendering_provider_id !== provider?._id &&
                format(new Date(panelEncounter?.service_date_from), 'yyyy-MM-dd') <=
                  format(new Date(), 'yyyy-MM-dd') && (
                  <CDropdownItem disabled={disableButtons} onClick={onAdditionalVisit}>
                    Additional Visit
                  </CDropdownItem>
                )}
            </>
          )}
        </CDropdownMenu>
      </CDropdown>
      {showVoid && (
        <Void
          visible={true}
          onClose={() => {
            setShowVoid(false)
          }}
          hasNote={Boolean(panelChart?._id)}
          onVoid={async (downloadNotesIfExist) => {
            if (panelChart?._id && downloadNotesIfExist) {
              // download notes first
              await downloadChartPDF(panelEncounter)
            }
            if (!disableButtons) {
              onVoid(panelEncounter)
            }
            setShowVoid(false)
          }}
        />
      )}
      {showRecall && (
        <RecallSubmissionBulk
          visible={true}
          onClose={() => setShowRecall(false)}
          onBulkRecall={() => {
            onRecall([panelEncounter])
            setShowRecall(false)
          }}
        />
      )}

      {showMessaging && (
        <ClaimocitySupportPopup
          visible={true}
          setVisible={setShowMessaging}
          activePatient={panelEncounter}
          page={'Provider Hold'}
          item={panelEncounter}
          refreshCensusTable={() => {
            dispatch(refreshEncounter({ encounterId: panelEncounter._id }))
            dispatch(resetProviderHold())
          }}
        />
      )}
      {showDelete && (
        <DeleteNote
          visible={true}
          onChange={() => {
            setShowDelete(false)
            dispatch(clearSidePanel())
          }}
          handleClose={() => setShowDelete(false)}
          chart_id={panelChart?._id}
        />
      )}
      {showClone && (
        <CloneEncounterDateModal
          onClose={() => setShowClone(false)}
          onChange={async (date) => {
            if (disableButtons) {
              return
            }
            await onClone(date)
            setShowClone(false)
          }}
        />
      )}
    </div>
  )
}
